import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, PrimaryButton, DefaultButton, MessageBar, MessageBarType, DetailsList, DetailsListLayoutMode, IColumn, SelectionMode } from "@fluentui/react";
import { AdminLabel, ErrorDialog, LinkButton } from "../components";
import { checkImportUsers, submitImportUsers } from "../../../api/admin/api_admin";
import { ImportUser } from "../../../api/admin/models_admin";

export const ImportUsers = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    const [pageState, setPageState] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // 確認画面用のデータ
    const [addedUsers, setAddedUsers] = useState<ImportUser[]>([]);
    const [deletedUsers, setDeletedUsers] = useState<ImportUser[]>([]);
    const [modifiedUsers, setModifiedUsers] = useState<ImportUser[]>([]);
    const [modifiedColumns, setModifiedColumns] = useState<Record<string, string[]>>({});
    const [jsonedCsvInfo, setJsonedCsvInfo] = useState<string>("");

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        setFile(selectedFile || null);
    };

    const handleCheckSubmit = async () => {
        if (!file) return;

        setSubmitting(true);
        setError(null);

        try {
            const result = await checkImportUsers({ file });

            if ("error" in result) {
                setError(result.error);
            } else {
                setAddedUsers(result.added_users || []);
                setDeletedUsers(result.deleted_users || []);
                setModifiedUsers(result.modified_users || []);
                setModifiedColumns(result.modified_columns || {});
                setJsonedCsvInfo(result.jsoned_csv_info || "");
                setPageState(1);
            }
        } catch (err) {
            setError("チェック処理中にエラーが発生しました");
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmSubmit = async () => {
        setSubmitting(true);
        setError(null);

        try {
            const result = await submitImportUsers({ jsoned_csv_info: jsonedCsvInfo });

            if (result.success) {
                navigate("/menu/users");
            } else {
                setError(result.message || "インポート処理に失敗しました");
            }
        } catch (err) {
            setError("インポート処理中にエラーが発生しました");
        } finally {
            setSubmitting(false);
        }
    };

    const getCellStyle = (userid: string, columnName: string) => {
        if (modifiedColumns[userid] && modifiedColumns[userid].includes(columnName)) {
            return { backgroundColor: 'yellow' };
        }
        return {};
    };

    const addedColumns: IColumn[] = [
        { key: 'userid', name: 'ユーザーID', fieldName: 'userid', minWidth: 100, maxWidth: 150 },
        { key: 'username', name: 'ユーザー名', fieldName: 'username', minWidth: 120, maxWidth: 200 },
        { key: 'username_alphabet', name: '姓名(ローマ字)', fieldName: 'username_alphabet', minWidth: 120, maxWidth: 200 },
        { key: 'password', name: 'パスワード', fieldName: 'password', minWidth: 100, maxWidth: 150 },
        { key: 'authority_level', name: '権限レベル', fieldName: 'authority_level', minWidth: 80, maxWidth: 100 },
        { key: 'groups_str', name: 'グループ', fieldName: 'groups_str', minWidth: 150, maxWidth: 250 }
    ];

    const deletedColumns: IColumn[] = [
        { key: 'userid', name: 'ユーザーID', fieldName: 'userid', minWidth: 100, maxWidth: 150 },
        { key: 'username', name: 'ユーザー名', fieldName: 'username', minWidth: 120, maxWidth: 200 },
        { key: 'username_alphabet', name: '姓名(ローマ字)', fieldName: 'username_alphabet', minWidth: 120, maxWidth: 200 },
        { key: 'authority_level', name: '権限レベル', fieldName: 'authority_level', minWidth: 80, maxWidth: 100 },
        { key: 'groups_str', name: 'グループ', fieldName: 'groups_str', minWidth: 150, maxWidth: 250 }
    ];

    const modifiedColumns_: IColumn[] = [
        { 
            key: 'userid', 
            name: 'ユーザーID', 
            fieldName: 'userid', 
            minWidth: 100, 
            maxWidth: 150,
            onRender: (item: ImportUser) => (
                <div style={getCellStyle(item.userid, 'userid')}>{item.userid}</div>
            )
        },
        { 
            key: 'username', 
            name: 'ユーザー名', 
            fieldName: 'username', 
            minWidth: 120, 
            maxWidth: 200,
            onRender: (item: ImportUser) => (
                <div style={getCellStyle(item.userid, 'username')}>{item.username}</div>
            )
        },
        { 
            key: 'username_alphabet', 
            name: '姓名(ローマ字)', 
            fieldName: 'username_alphabet', 
            minWidth: 120, 
            maxWidth: 200,
            onRender: (item: ImportUser) => (
                <div style={getCellStyle(item.userid, 'username_alphabet')}>{item.username_alphabet}</div>
            )
        },
        { 
            key: 'password', 
            name: 'パスワード', 
            fieldName: 'password', 
            minWidth: 100, 
            maxWidth: 150,
            onRender: (item: ImportUser) => (
                <div style={getCellStyle(item.userid, 'password')}>{item.password || ''}</div>
            )
        },
        { 
            key: 'authority_level', 
            name: '権限レベル', 
            fieldName: 'authority_level', 
            minWidth: 80, 
            maxWidth: 100,
            onRender: (item: ImportUser) => (
                <div style={getCellStyle(item.userid, 'authority_level')}>{item.authority_level}</div>
            )
        },
        { 
            key: 'groups_str', 
            name: 'グループ', 
            fieldName: 'groups_str', 
            minWidth: 150, 
            maxWidth: 250,
            onRender: (item: ImportUser) => (
                <div style={getCellStyle(item.userid, 'groups')}>{item.groups_str}</div>
            )
        }
    ];

    if (pageState === 0) {
        return (
            <Stack tokens={{ childrenGap: 20 }}>
                <AdminLabel text="ユーザーインポート" />

                {error && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        onDismiss={() => setError(null)}
                        dismissButtonAriaLabel="閉じる"
                    >
                        {error}
                    </MessageBar>
                )}

                <Stack tokens={{ childrenGap: 15 }}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="text/csv"
                        onChange={handleFileChange}
                        style={{ marginBottom: '10px' }}
                    />
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton
                            text="次へ"
                            onClick={handleCheckSubmit}
                            disabled={!file || submitting}
                        />
                        <LinkButton link_to="/menu/users" text="戻る" />
                    </Stack>
                </Stack>
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="ユーザーインポート確認" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            {addedUsers.length > 0 && (
                <Stack tokens={{ childrenGap: 10 }}>
                    <h3>追加</h3>
                    <DetailsList
                        items={addedUsers}
                        columns={addedColumns}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={SelectionMode.none}
                        isHeaderVisible={true}
                    />
                </Stack>
            )}

            {deletedUsers.length > 0 && (
                <Stack tokens={{ childrenGap: 10 }}>
                    <h3>削除</h3>
                    <DetailsList
                        items={deletedUsers}
                        columns={deletedColumns}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={SelectionMode.none}
                        isHeaderVisible={true}
                    />
                </Stack>
            )}

            {modifiedUsers.length > 0 && (
                <Stack tokens={{ childrenGap: 10 }}>
                    <h3>変更</h3>
                    <DetailsList
                        items={modifiedUsers}
                        columns={modifiedColumns_}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={SelectionMode.none}
                        isHeaderVisible={true}
                    />
                </Stack>
            )}

            <Stack horizontal tokens={{ childrenGap: 10 }}>
                <PrimaryButton
                    text="変更を確定"
                    onClick={handleConfirmSubmit}
                    disabled={submitting}
                />
                <DefaultButton
                    text="キャンセル"
                    onClick={() => {
                        setPageState(0);
                        setFile(null);
                        if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                        }
                    }}
                    disabled={submitting}
                />
            </Stack>
        </Stack>
    );
}